import { useState, useEffect, useMemo, useCallback } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import Link from "next/link";
import cn from "classnames";
import { IDashboardHeader } from "@/types/all.types";
import { Storage, closeChatPopup, everyTrue } from "@/helper/helper";
import { UpgradePopup } from "../Modal/UpgradePopup";
import { useMediaQuery } from "react-responsive";
import useAccountNewShow from "@/hooks/user/account/useAccountNewShow";
import { IoIosHelpCircleOutline } from "react-icons/io";
import LimitIsReachModal from "../Modal/LimitIsReach/LimitIsReachModal";
import { helpCenterLink } from "@/constant";
import { useArriveBilling } from "@/states/billing.state";
import { useNav } from "@/states/nav.state";
import { useIsExpired } from "@/states/user.state";
import { REDIRECT_TYPE_LIST_PAGE, redirectDashboardPages, redirectSelectTypePages } from "./shared/data/data";
import { cookiePersistStorage } from "@/helper/common.helper";
import { BsArrowLeftShort } from "react-icons/bs";
import { UpgradePopupMobile } from "@/components/page-modules/dashboard/Modal/UpgradePopupMobile/UpgradePopupMobile";
import { When } from "@/components/common/When";
import IFThen from "@/components/common/IFThen";
import GlobalStyle from "@/components/common/GlobalStyle/GlobalStyle";
import cls from "@/styles/dashboard/layout/Header.module.scss";
const Header = ({
  showProfile,
  button,
  dashboard
}: IDashboardHeader) => {
  // hooks
  const isMobile = useMediaQuery({
    maxWidth: 768
  });
  const router = useRouter();

  // vars
  const access_token = cookiePersistStorage.getItem("access_token");

  // states
  const arriveBilling = useArriveBilling();
  const {
    isShow,
    setIsShow
  } = useNav();
  const {
    expiredConfig,
    setParams
  } = useIsExpired();
  const [isAccountDropdown, setIsAccountDropdown] = useState<boolean>(false);

  // api
  const {
    accountNewData
  } = useAccountNewShow();
  const accountInfo = accountNewData?.data;
  useEffect(() => {
    setParams({
      isExpired: false,
      isReach: false,
      isDublicate: true
    });
    return () => {
      closeChatPopup();
    };
  }, [router.pathname]);
  const handleChangeBack = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParam = searchParams.get("redirect");
    const currentPath = router.pathname;
    const isDashboardRedirect = queryParam === "dashboard";
    const isPricingPage = currentPath === "/dashboard/pricing";
    const isCardModalOpen = searchParams.has("cardModal");
    const isSuccessfulOffer = currentPath.includes("successful-offer");
    if (isDashboardRedirect) {
      return router.push("/dashboard");
    }
    if (isPricingPage) {
      if (arriveBilling.isArriveBilling) {
        arriveBilling.setIsArriveBilling(false);
        return router.push("/dashboard/user/billing");
      }
      return router.push(access_token ? "/dashboard" : "/login");
    }
    if (isCardModalOpen || isSuccessfulOffer) {
      return router.push("/dashboard/user/billing/payment-details");
    }
    router.back();
  }, [router, arriveBilling.isArriveBilling, access_token]);
  const redirectSelectType = () => {
    Storage().removeStorage("qrShowData");
    router.push(REDIRECT_TYPE_LIST_PAGE);
  };
  const renderBackButton = useMemo(() => {
    const isBuilderStep = redirectSelectTypePages.some(regex => regex.test(router.pathname));
    const isDashboardPage = Boolean(router.query.id) || redirectDashboardPages.includes(router.pathname);
    const {
      label,
      handler
    } = (() => {
      if (isDashboardPage) return {
        label: "Dashboard",
        handler: () => router.push("/dashboard")
      };
      if (isBuilderStep) return {
        label: "Select Type",
        handler: redirectSelectType
      };
      return {
        label: "Back",
        handler: handleChangeBack
      };
    })();
    return <div className={cls.Menu}>
        <button className={cls.Button} onClick={handler}>
          <BsArrowLeftShort color="#0A9396" size={25} />
          {label}
        </button>
      </div>;
  }, [router.pathname, router.query.id]);
  return <header className={cls.Wrapper} data-sentry-component="Header" data-sentry-source-file="index.tsx">
      <When condition={expiredConfig.isReach} data-sentry-element="When" data-sentry-source-file="index.tsx">
        <GlobalStyle data-sentry-element="GlobalStyle" data-sentry-source-file="index.tsx" />
      </When>
      <When condition={isAccountDropdown} data-sentry-element="When" data-sentry-source-file="index.tsx">
        <div className={cls.AccountOverlay} onClick={() => setIsAccountDropdown(false)} />
      </When>

      <div className={cls.Container}>
        <div className={cls.Flex}>
          <div className={cls.Left}>
            <div className={cls.Menu}>
              <When condition={everyTrue(button === "hide", dashboard === "show")} data-sentry-element="When" data-sentry-source-file="index.tsx">
                <div className={cls.ButtonDashboardNav} onClick={() => setIsShow(isShow === "open" ? "close" : "open")}>
                  <span className={cn(cls.ArrowSvgBox, {
                  [cls.active]: isShow !== 'open'
                })}>
                    <img src={"/dashboard-assets/sidebar.svg"} />
                  </span>
                </div>
              </When>
            </div>
            <When condition={!!dashboard} data-sentry-element="When" data-sentry-source-file="index.tsx">
              <div className={cls.Divider}></div>
            </When>
            <Link className={cls.logoDiv} href={"/dashboard"} data-sentry-element="Link" data-sentry-source-file="index.tsx">
              <Image src={"/common/dashboard-logo.svg"} width={224} height={28} alt="Logo" className={cls.logo} data-sentry-element="Image" data-sentry-source-file="index.tsx" />
            </Link>
            <When condition={!dashboard} data-sentry-element="When" data-sentry-source-file="index.tsx">
              <div className={cls.Divider}></div>
            </When>
            <When condition={button !== "hide"} data-sentry-element="When" data-sentry-source-file="index.tsx">
              {renderBackButton}
            </When>
          </div>
          <When condition={!showProfile} data-sentry-element="When" data-sentry-source-file="index.tsx">
            <div className={cls.Profile}>
              <div className={cls.RightUserActions}>
                <a target={"_blank"} href={helpCenterLink}>
                  <IoIosHelpCircleOutline size={24} color="#0A9396" data-sentry-element="IoIosHelpCircleOutline" data-sentry-source-file="index.tsx" />
                  <span>Help</span>
                </a>
              </div>
            </div>
          </When>
        </div>
      </div>

      <When condition={everyTrue(expiredConfig.isExpired, !router.pathname.includes("/pricing"))} data-sentry-element="When" data-sentry-source-file="index.tsx">
        <IFThen condition={isMobile} Then={<UpgradePopupMobile plan_uuid={accountInfo?.plan_uuid} />} Else={<UpgradePopup plan_uuid={accountInfo?.plan_uuid} />} data-sentry-element="IFThen" data-sentry-source-file="index.tsx" />
      </When>

      <When condition={expiredConfig.isReach} data-sentry-element="When" data-sentry-source-file="index.tsx">
        <LimitIsReachModal plan_uuid={accountInfo?.plan_uuid} data-sentry-element="LimitIsReachModal" data-sentry-source-file="index.tsx" />
      </When>
    </header>;
};
export default Header;